import css from './MultiSelect.component.scss';
import html from './MultiSelect.component.html';

/**
 * A menu of values from which the user may select multiple values simultaneously.
 * 
 * Attributes:
 * * `customstyle` - CSS rules to inject into the menu.
 * * `filter` - Controls whether the menu will filter the visible options when the user enters text into the filter input.
 * Boolean attribute.
 * * `highlightonly` - Hides all checkboxes and causes only highlighted values to be considered selected.
 * Boolean attribute.
 * * `single` - Restricts selection to a single value. Boolean attribute.
 * * `sortable` - Displays a handle on each value enabling the user to change its order in the menu
 * by clicking (or long-pressing on touch screens) and dragging. Boolean attribute.
 * 
 * Events:
 * * `search` - Fired when the user enters text into the filter input.
 * The event detail contains the text entered.
 * * `sort` - Fired when the user sorts the menu.
 * The event detail contains the new, full set of ordered values as an array of strings.
 * * `valuechange` - Fired when the user selects or deselects a value.
 * The event detail contains the new, full set of selected values as an array of strings.
 */
export class MultiSelect extends HTMLElement {
    /**
     * Initializes the element.
     */
    constructor() {
        super();

        const style = document.createElement('style');

        const template = document.createElement('template');

        style.textContent = css;

        template.innerHTML = html;

        this.attachShadow({ mode: 'open' });

        /**
         * The value in the menu that the user most recently focused without holding the shift key.
         * 
         * Used as the "start" value in all operations highlighting a range of values (e.g. dragging and shift-click).
         * 
         * @type {HTMLElement}
         * 
         * @private
         */
        this.highlightOrigin = undefined;

        /**
         * The time when the most recent value click handler completed.
         * 
         * @private
         */
        this.lastClick = undefined;

        /**
         * Indicates whether the next focusout event should trigger an unhighlight.
         * 
         * @private
         */
        this.nextBlurEnabled = true;

        /**
         * The timer for a pending search for values within the menu.
         * 
         * @private
         */
        this.searchTimer = undefined;

        /**
         * The timer for a pending unhighlight of all values in the menu.
         * 
         * @private
         */
        this.unhighlightTimer = undefined;

        const { shadowRoot } = this;

        shadowRoot.append(style, template.content.cloneNode(true));

        const filter = shadowRoot.getElementById('filter');
        const values = shadowRoot.getElementById('values');

        this.clearHighlight();

        filter.addEventListener('input', event => this.handleInput(event));

        values.addEventListener('click', event => this.handleClick(event));
        values.addEventListener('dragenter', event => this.handleDragEnter(event));
        values.addEventListener('dragover', handleDragOver);
        values.addEventListener('dragstart', event => this.handleDragStart(event));
        values.addEventListener('drop', event => this.handleDrop(event));
        values.addEventListener('focusin', event => this.handleFocusIn(event));
        values.addEventListener('focusout', () => this.handleFocusOut());
        values.addEventListener('keydown', event => this.handleKeyDown(event));
        values.addEventListener('mousedown', event => this.handleMouseDown(event));
    }

    /**
     * The values currently selected in the menu.
     * 
     * @public
     */
    get value() {
        const checkedText = this.shadowRoot.querySelectorAll('input:checked + label span');

        return Array
            .from(checkedText)
            .map(({ textContent }) => textContent);;
    }

    /**
     * Sets the values selected in the menu.
     * 
     * Any specified values not currently displayed in the menu will be prepended to the available values.
     */
    set value(value) {
        const { shadowRoot } = this;

        value = [...value];

        const inputs = shadowRoot.querySelectorAll('input');

        for (const checkbox of inputs) {
            const index = value.indexOf(checkbox.nextElementSibling.children[0].textContent);

            const select = index >= 0;

            checkbox.checked = select;

            if (select) {
                value.splice(index, 1);
            }
        }

        for (let i = 0; i < value.length; i++) {
            const element = this.createValue(values[i], inputs.length + i);

            element.querySelector('input').checked = true;

            shadowRoot.getElementById('values').prepend(element);
        }
    }

    /**
     * The values currently contained in the menu.
     * 
     * @public
     */
    get values() {
        const values = this.shadowRoot.querySelectorAll('label span');

        return Array.from(values).map(({ textContent }) => textContent);
    }

    /**
     * Sets the values to display in the menu.
     */
    set values(values) {
        const elements = values.map((value, i) => this.createValue(value, i));

        const { shadowRoot } = this;

        const select = shadowRoot.getElementById('values');

        select.innerHTML = '';

        select.append(...elements);
    }

    /**
     * The attributes observed for changes by the element.
     */
    static get observedAttributes() { return ['customstyle', 'single', 'sortable']; }

    /**
     * Highlights the specified values in the menu.
     * 
     * @param {string[]} values The values to highlight.
     * 
     * @public
     */
    highlight(values) {
        const elements = Array
            .from(this.shadowRoot.querySelectorAll('.value'))
            .filter(value => values.includes(value.querySelector('span').textContent));

        for (const value of elements) {
            this.highlightValue(value);
        }
    }

    /**
     * Highlights every value in the menu.
     * 
     * @public
     */
    highlightAll() {
        for (const value of this.shadowRoot.querySelectorAll('.value')) {
            this.highlightValue(value);
        }
    }

    /**
     * Runs when an attribute observed by the element changes.
     * 
     * @param {string} name The name of the changed attribute.
     * @param {string} _ The old value of the attribute.
     * @param {string} newValue The new value of the attribute.
     */
    attributeChangedCallback(name, _, newValue) {
        switch (name) {
            case 'customstyle':
                this.shadowRoot.getElementById('custom').innerHTML = newValue;
                break;
            case 'single':
                this.updateSingle(newValue);
                break;
            case 'sortable':
                this.updateSortable(newValue);
                break;
        }
    }

    /**
     * Cancels a pending unhighlight action that was scheduled to occur as the result of a focusout event, if any.
     * 
     * @private
     */
    cancelBlur() {
        clearTimeout(this.unhighlightTimer);
    }

    /**
     * Ensures consistent and simple checking of all highlighted values, including the one just clicked.
     * Needed because on click, browsers report the checked state differently depending on which element is clicked (label or checkbox).
     * Once all browsers support the beforeinput event,
     * this is worth revisiting to see if this entire function can be replaced with just a call to {@link checkHighlighted}.
     * 
     * @param {MouseEvent} event The click event.
     * @param {HTMLInputElement} checkbox The clicked checkbox.
     * 
     * @private
     */
    checkClicked(event, { checked }) {
        const { shiftKey, target } = event;

        const isCheckbox = target instanceof HTMLInputElement;

        const check = isCheckbox ? checked : !checked;

        if (!shiftKey) { // Shift behavior is handled on mousedown
            event.preventDefault();

            // Because the default behavior is prevented,
            // browsers do not honor changes to the checked state made directly within the event handler.
            setTimeout(() => this.checkHighlighted(check), 0);
        }
    }

    /**
     * Checks or unchecks all of the currently highlighted checkboxes.
     * 
     * @param {boolean?} check Indicates whether to check the values. Defaults to true.
     * 
     * @private
     */
    checkHighlighted(check = true) {
        const { shadowRoot } = this;

        for (const checkbox of shadowRoot.querySelectorAll('.highlight input')) {
            checkbox.checked = check;
        }

        this.sendValueChange();
    }

    /**
     * Unhighlights all currently highlighted values.
     * 
     * If the highlightonly attribute is specified on this element, also unchecks them.
     * 
     * @private
     */
    clearHighlight() {
        const highlightOnly = this.hasAttribute('highlightonly');

        this.highlightOrigin = undefined;

        for (const value of this.getHighlighted()) {
            value.classList.remove('highlight');

            if (highlightOnly) {
                value.querySelector('input').checked = false;
            }
        }
    }

    /**
     * Creates an element representing a single selectable value within the menu.
     * 
     * @param {string} value The value text.
     * @param {number} id An identifier for the value within the menu.
     * 
     * @private
     */
    createValue(value, id) {
        const draggable = !this.hasAttribute('single');

        const element = document.createElement('div');

        const idAttribute = `value-checkbox-${id}`;

        element.className = 'value';

        element.draggable = draggable;

        element.innerHTML = `
            <input id='${idAttribute}' type='checkbox'>
            
            <label for='${idAttribute}'>
                <span>${value}</span>
            </label>
    
            <button class='handle' draggable='true' title='Click and drag to reorder.'>=</button>
        `;

        return element;
    }

    /**
     * Filters the currently displayed values to only those containing the specified value.
     * 
     * @param {string} value The search value.
     * 
     * @private
     */
    filter(value) {
        const { shadowRoot } = this;

        value = value.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');

        const contains = new RegExp(value, 'i');

        for (const valueElement of shadowRoot.querySelectorAll('.value')) {
            const match = contains.test(valueElement.querySelector('label span').innerText);

            valueElement.classList.toggle('filtered', !match);
        }
    }

    /**
     * Retrieves the currently highlighted values.
     * 
     * @private
     */
    getHighlighted() {
        return this.shadowRoot.querySelectorAll('.highlight');
    }

    /**
     * Runs when the user presses the down arrow key inside the menu.
     * 
     * Focuses and highlights the next value in the menu instead of the currently focused value, if any.
     * 
     * @param {KeyboardEvent} event The keyboard event.
     * 
     * @private
     */
    handleArrowDown(event) {
        const { shiftKey, target } = event;

        event.preventDefault();

        this.tryNavigate(target.parentNode.nextSibling, shiftKey);
    }

    /**
     * Runs when the user presses the up arrow key inside the menu.
     * 
     * Focuses and highlights the previous value in the menu instead of the currently focused value, if any.
     * 
     * @param {KeyboardEvent} event The keyboard event.
     * 
     * @private
     */
    handleArrowUp(event) {
        const { shiftKey, target } = event;

        event.preventDefault();

        this.tryNavigate(target.parentNode.previousSibling, shiftKey);
    }

    /**
     * Runs when the user clicks inside the menu.
     * 
     * If the highlightonly attribute is specified on this element,
     * prevents the value's checkbox from being checked because it is already checked when highlighted.
     * 
     * @param {MouseEvent} event The click event.
     * 
     * @private
     */
    handleClick(event) {
        const { shiftKey, target } = event;

        const { classList } = target;

        const value = classList.contains('value') ? target : target.parentElement;

        const checkbox = value.querySelector('input');

        // Only respond once per physical click (can receive multiple events depending on which element is clicked).
        if (new Date() - this.lastClick < 100) {
            return;
        }

        if (classList.contains('handle')) {
            return;
        }

        checkbox.focus(); // ensures highlight is not cleared via a focusout

        if (this.hasAttribute('highlightonly')) {
            event.preventDefault();

            return;
        }

        this.nextBlurEnabled = true;

        // Ensures consistent and simple checking of all highlighted values (see function notes).
        this.checkClicked(event, checkbox);

        if (shiftKey) {
            event.preventDefault();
        }

        this.lastClick = new Date();
    }

    /**
     * Runs when the user drags into the menu or one of its descendants.
     * 
     * Responds only when the user drags into the root element of a value or one of its immediate children.
     * 
     * Highlights all values from the current highlight origin to the value into which the user dragged,
     * considering the direction of the drag, and unhighlights all other values.
     * 
     * @param {DragEvent} event The drag event.
     * 
     * @private
     */
    handleDragEnter({ target }) {
        if (!target.classList?.contains('value')) {
            const { parentElement } = target;

            if (parentElement?.classList.contains('value')) {
                target = parentElement;
            } else {
                return;
            }
        }

        const { highlightOrigin, shadowRoot } = this;

        const { nextElementSibling, previousElementSibling } = target;

        const values = Array.from(shadowRoot.querySelectorAll('.value'));

        const offset = values.indexOf(target) - values.indexOf(highlightOrigin);

        function unhighlight(value) {
            if (value) {
                toggleHighlight(value, false);
            }
        }

        this.highlightTo(target);

        if (offset > 0) {
            unhighlight(nextElementSibling);
        } else if (offset < 0) {
            unhighlight(previousElementSibling);
        } else {
            unhighlight(nextElementSibling);
            unhighlight(previousElementSibling);
        }
    }

    /**
     * Runs when the user starts dragging a value or one of its descendants.
     * 
     * Enables the user to highlight multiple values,
     * or to reorder a value using its handle element.
     * 
     * @param {DragEvent} event The drag event.
     * 
     * @private
     */
    handleDragStart(event) {
        const { dataTransfer, target } = event;

        if (target.classList.contains('handle')) {
            event.preventDefault();

            return;
        }

        dataTransfer.setDragImage(document.createElement('img'), 0, 0);

        this.cancelBlur();

        this.clearHighlight();

        this.highlightOrigin = target;

        this.highlightValue(target);
    }

    /**
     * Runs when the user drops on the menu or one of its descendants.
     * 
     * Selects the highlighted values.
     * 
     * @param {DragEvent} event The drop event.
     * 
     * @private
     */
    handleDrop(event) {
        const { target } = event;

        const checkbox = target.classList.contains('value') ? target : target.parentElement.querySelector('input');

        event.preventDefault();

        this.checkHighlighted();

        this.nextBlurEnabled = false;

        checkbox.focus();
    }

    /**
     * Runs when the user focuses the menu or one of its descendants.
     * 
     * Responds only when the user focuses a value's checkbox.
     * 
     * Highlights the focused value.
     * 
     * @param {FocusEvent} event The focusin event.
     * 
     * @private
     */
    handleFocusIn({ target }) {
        const { classList, parentNode } = target;

        if (classList.contains('handle') || target === this.shadowRoot.getElementById('values')) {
            return;
        }

        this.highlightValue(parentNode);

        if (!this.highlightOrigin) {
            this.highlightOrigin = parentNode;
        }

        if (this.hasAttribute('highlightonly')) {
            this.sendValueChange();
        }
    }

    /**
     * Runs when the user focuses out of the menu or one of its descendants.
     * 
     * Sets a timer to unhighlight all values,
     * which the element should cancel on the next focusin so the highlight is not cleared unnecessarily.
     * 
     * @param {FocusEvent} event The focusout event.
     * 
     * @private
     */
    handleFocusOut() {
        if (this.nextBlurEnabled) {
            this.unhighlightTimer = setTimeout(() => this.clearHighlight(), 100);
        }

        this.nextBlurEnabled = !this.hasAttribute('highlightonly');
    }

    /**
     * Runs when the user enters text into the filter input.
     * 
     * Filters the visible values in the menu to those containing the input's value,
     * after a debounce timer.
     * 
     * @param {InputEvent} event The input event.
     * 
     * @private
     */
    handleInput({ target }) {
        const debounce = 100;

        clearTimeout(this.searchTimer);

        this.searchTimer = setTimeout(() => this.search(target.value), debounce);
    }

    /**
     * Runs when the user presses a key within the menu.
     * 
     * @param {KeyboardEvent} event The keydown event.
     * 
     * @private
     */
    handleKeyDown(event) {
        switch (event.code) {
            case 'ArrowDown':
                this.handleArrowDown(event);
                break;

            case 'ArrowUp':
                this.handleArrowUp(event);
                break;

            case 'Tab':
                this.handleTab();
                break;
        }
    }

    /**
     * Runs when the user depresses a mouse button within the menu.
     * 
     * Responds only if a value element or one of its descendants was clicked.
     * 
     * If the clicked element was a drag handle,
     * enables the user to begin sorting the value.
     * 
     * Otherwise, performs custom handling for the clicked value (see handleValueMouseDown).
     * 
     * @param {MouseEvent} event The mousedown event.
     * 
     * @private
     */
    handleMouseDown(event) {
        const { shadowRoot } = this;

        const { target } = event;

        if (target.classList.contains('handle')) {
            this.startSort(target.parentElement);
        } else if (target !== shadowRoot.getElementById('values')) {
            this.handleValueMouseDown(event);
        }
    }

    /**
     * Runs when the mouse leaves the menu.
     * 
     * Ends the user's current sort operation, if any.
     * 
     * @private
     */
    handleMouseLeave() {
        this.tryFinishSort();
    }

    /**
     * Runs when the mouse enters the menu or one of its descendants.
     * 
     * If the mouse entered a value element or one of its descendants,
     * moves the value that the user is currently sorting, if any,
     * into the entered value's position.
     * 
     * @param {MouseEvent} event The mouseover event.
     * 
     * @private
     */
    handleMouseOver({ clientY, target }) {
        this.trySort(target, clientY);
    }

    /**
     * Runs when the user releases a mouse button.
     * 
     * Ends the user's current sort operation, if any.
     * 
     * @private
     */
    handleMouseUp() {
        this.tryFinishSort();
    }

    /**
     * Runs when the user presses the Tab key.
     * 
     * Clears any existing highlight.
     * 
     * @private
     */
    handleTab() {
        this.clearHighlight();

        this.nextBlurEnabled = false;
    }

    /**
     * Runs when the user stops touching the menu.
     * 
     * Ends the user's current sort operation, if any.
     * 
     * @private
     */
    handleTouchEnd() {
        this.tryFinishSort();
    }

    /**
     * Runs when the user moves a touch that began within the menu.
     * 
     * Responds only if the user is currently sorting a value.
     * 
     * If the touch moved into a value element or one of its descendants,
     * moves the value that the user is currently sorting into the touched value's position.
     * 
     * @param {TouchEvent} event The touchmove event.
     * 
     * @private
     */
    handleTouchMove(event) {
        const { shadowRoot } = this;

        const [touch] = event.touches;

        const { clientX, clientY } = touch;

        if (!shadowRoot.querySelector('.sorting')) {
            return;
        }

        const element = shadowRoot.elementFromPoint(clientX, clientY);

        event.preventDefault();

        this.trySort(element, clientY);
    }

    /**
     * Runs when the user depresses a mouse button within a value element or one of its descendants
     * (except its drag handle).
     * 
     * If the clicked value was not already highlighted, highlights it.
     * 
     * If none of the ctrl, meta, and shift keys is held,
     * unhighlights all other values.
     * 
     * If the shift key is held,
     * also highlights all values from the current highlight origin through the clicked value,
     * unhighlighting all others.
     * If the highlight origin was checked,
     * also checks the values within this range.
     * 
     * All multi-selection behavior is disabled if this element has the `single` attribute.
     * 
     * @param {MouseEvent} event The mousedown event.
     * 
     * @private
     */
    handleValueMouseDown({ ctrlKey, metaKey, shiftKey, target }) {
        const addKey = ctrlKey || metaKey;

        const { highlightOrigin } = this;

        const isCheckbox = target instanceof HTMLInputElement;

        const single = this.hasAttribute('single');

        const value = target.classList.contains('value') ? target : target.parentElement;

        const add = addKey && !single;

        const highlighted = isHighlighted(value);

        const series = shiftKey && !single;

        this.nextBlurEnabled = false;

        if (series) {
            if (highlightOrigin) {
                this.highlightTo(value);

                if (highlightOrigin.querySelector('input').checked) {
                    this.checkHighlighted();
                }
            }
        } else if (add) {
            this.highlightValue(value);
        } else {
            if (isCheckbox) {
                if (!highlighted) {
                    this.clearHighlight();

                    this.highlightOrigin = value;
                }
            } else {
                this.clearHighlight();

                this.highlightOrigin = value;
            }

            this.highlightValue(value);
        }

        if (this.hasAttribute('highlightonly')) {
            this.sendValueChange();
        }
    }

    /**
     * Highlights all values from the current highlight origin through the specified value
     * and unhighlights all other values within any previously selected range.
     * 
     * @param {HTMLElement} value The last value to highlight.
     * 
     * @private
     */
    highlightTo(value) {
        const { highlightOrigin, shadowRoot } = this;

        const values = Array.from(shadowRoot.querySelectorAll('.value'));

        const fromIndex = values.indexOf(highlightOrigin);
        const toIndex = values.indexOf(value);

        const [highlightStart, highlightEnd] = [fromIndex, toIndex].sort((a, b) => a - b);

        function unhighlightRange(from, increment) {
            for (let i = from + increment; values[i]?.classList.contains('highlight'); i += increment) {
                values[i].classList.remove('highlight');
            }
        }

        unhighlightRange(highlightEnd, 1);
        unhighlightRange(highlightStart, -1);

        for (let i = highlightStart; i <= highlightEnd; i++) {
            values[i].classList.add('highlight');
        }
    }

    /**
     * Highlights the specified value element in the menu.
     * 
     * @param {HTMLElement} value The value to highlight.
     * 
     * @private
     */
    highlightValue(value) {
        toggleHighlight(value, true);

        if (this.hasAttribute('highlightonly')) {
            value.querySelector('input').checked = true;
        }
    }

    /**
     * Dispatches an event indicating that the user typed into the filter input.
     * 
     * @param {string} search The value entered into the filter input.
     * 
     * @private
     */
    search(search) {
        const event = new CustomEvent('search', { detail: search });

        this.dispatchEvent(event);

        if (this.hasAttribute('filter')) {
            this.filter(search);
        }
    }

    /**
     * Dispatches an event indicating that the user changed the selected values.
     * 
     * @private
     */
    sendValueChange() {
        this.dispatchEvent(new CustomEvent('valuechange', { detail: this.value }));
    }

    /**
     * Enables the user to begin sorting the value containing the specified handle element.
     * 
     * @param {HTMLElement} handle The handle element of the value to sort.
     * 
     * @private
     */
    startSort({ classList }) {
        classList.add('sorting');

        this.clearHighlight();

        this.classList.add('sorting');
    }

    /**
     * Completes the value sort that the user is currently performing, if any.
     * 
     * @private
     */
    tryFinishSort() {
        const sorting = this.shadowRoot.querySelector('.sorting');

        if (!sorting) {
            return;
        }

        sorting.classList.remove('sorting');

        this.classList.remove('sorting');

        this.dispatchEvent(new CustomEvent('sort', { detail: this.values }));
    }

    /**
     * Focuses and highlights the specified value if it is defined,
     * in response to an arrow key press.
     * 
     * If the shift key is held, adds the value to the current highlight.
     * Otherwise, clears any existing highlight.
     * 
     * Shift-key behavior is disabled if this element has the `single` attribute.
     * 
     * @param {HTMLElement | undefined} value The value to which the user is attempting to navigate.
     * @param {boolean} shiftKey Indicates whether the shift key was held when the user attempted to navigate.
     * 
     * @private
     */
    tryNavigate(value, shiftKey) {
        if (!value) {
            return;
        }

        this.nextBlurEnabled = false;

        if (shiftKey && !this.hasAttribute('single')) {
            this.highlightTo(value);
        } else {
            this.clearHighlight();

            this.highlightOrigin = value;
        }

        value.querySelector('input').focus();
    }

    /**
     * Moves the value that the user is currently sorting, if any,
     * to the position of the value associated with the specified element.
     * 
     * Scrolls the menu up/down if the event triggering the sort occurred near the visible top/bottom of the menu.
     * 
     * @param {HTMLElement} element The value element, or child of the value element,
     * to whose position the element currently being sorted will be moved.
     * @param {number} clientY The Y-coordinate where the event triggering this sort occurred,
     * relative to the viewport.
     * 
     * @private
     */
    trySort(element, clientY) {
        const isValue = element.classList.contains('value');

        const { shadowRoot } = this;

        const value = isValue ? element : element.parentElement;

        const values = shadowRoot.getElementById('values');

        if (element === values || value.classList.contains('sorting')) {
            return;
        }

        const sorting = shadowRoot.querySelector('.sorting');

        if (!sorting) {
            return;
        }

        const scrollHeight = .15;

        const valueElements = Array.from(values.children);

        const menuRectangle = values.getBoundingClientRect();

        const reference = valueElements.indexOf(sorting) > valueElements.indexOf(value)
            ? value
            : value.nextElementSibling;

        const menuHeight = menuRectangle.bottom - menuRectangle.top;

        const sortOffset = clientY - menuRectangle.top;

        const sortRatio = sortOffset / menuHeight;

        const bottom = sortRatio > (1 - scrollHeight);
        const top = sortRatio < scrollHeight;

        values.insertBefore(sorting, reference);

        if (bottom || top) {
            let scrollDistance = 35;

            if (top) {
                scrollDistance = -scrollDistance;
            }

            values.scrollTo({ behavior: 'smooth', top: values.scrollTop + scrollDistance });
        }
    }

    /**
     * Scrolls the menu up/down if the selected menu item moves past the top/bottom of the menu.
     * 
     * @param {string} value The value of the element whose position will determine if the menu should be scrolled.
     * 
     * @public
     */
     tryScrollToValue(value) {

        const { shadowRoot } = this;

        const valuesElem = shadowRoot.getElementById('values');

        const element = Array
            .from(valuesElem.querySelectorAll('.value'))
            .find(x => value.includes(x.querySelector('span').textContent));

        const itemRect = element.getBoundingClientRect();
        const menuRect = valuesElem.getBoundingClientRect();
   
        const bottom = itemRect.bottom >= menuRect.bottom;
        const top = itemRect.top <= menuRect.top;        

        if (bottom || top) {
            let scrollDistance = itemRect.height + 10;

            if (top) {
                scrollDistance = -scrollDistance;
            }

            valuesElem.scrollTo({ behavior: 'smooth', top: valuesElem.scrollTop + scrollDistance });
        }   
    }

    /**
     * Updates the element to the specified value of the `single` attribute.
     * 
     * If the attribute is specified,
     * enables dragging to select values.
     * 
     * @param {string} single The value of the `single` attribute.
     * 
     * @private
     */
    updateSingle(single) {
        const draggable = typeof single !== 'string';

        for (const value of this.shadowRoot.querySelectorAll('.value')) {
            value.draggable = draggable;
        }
    }

    /**
     * Updates the element to the specified value of the `sortable` attribute.
     * 
     * If the attribute is specified,
     * enables the mouse and touch events supporting value sorting.
     * 
     * @param {string} sortable The value of the `sortable` attribute.
     * 
     * @private
     */
    updateSortable(sortable) {
        const values = this.shadowRoot.getElementById('values');

        if (typeof sortable === 'string') {
            values.addEventListener('mouseleave', event => this.handleMouseLeave(event));
            values.addEventListener('mouseover', event => this.handleMouseOver(event));
            values.addEventListener('mouseup', () => this.handleMouseUp());
            values.addEventListener('touchend', () => this.handleTouchEnd());
            values.addEventListener('touchmove', event => this.handleTouchMove(event));
        }
    }
}

/**
 * Runs when the user drags over a MultiSelect or one of its descendants.
 * 
 * Allows the drag.
 * 
 * @param {DragEvent} event The dragover event.
 */
function handleDragOver(event) {
    event.preventDefault();
}

/**
 * Indicates whether the specified value element is highlighted.
 * 
 * @param {HTMLElement} value The value element to check.
 */
function isHighlighted(value) {
    return value.classList.contains('highlight');
}

/**
 * Toggles the highlighted state of the specified value element to match the specified boolean.
 * 
 * @param {HTMLElement} value The value element to highlight or unhighlight.
 * @param {boolean} highlight If true, the value is highlighted. Otherwise it is unhighlighted.
 */
function toggleHighlight({ classList }, highlight) {
    classList.toggle('highlight', highlight);
}

customElements.define('multi-select', MultiSelect);
